// Score calculation utilities for the NEW seller incentive program
// 
// NEW Formula (Contest System):
// SCORE = membership_sale_points + redemption_points + passive_points + savings_points + milestone_points + achievement_points
//
// Where:
// - membership_sale_points: Bronce=10, Plata=20, Oro=40 per sale
// - redemption_points: 8 pts per redemption processed
// - passive_points: 2 pts per redemption of memberships you sold
// - savings_points: 10 pts per ₡10,000 saved
// - milestone_points: cycle-based milestones (first sale, consistency, etc.)
// - achievement_points: permanent achievement points

export interface ScoreBreakdown {
  membershipSales: number;   // Points from membership sales (Bronce×10 + Plata×20 + Oro×40)
  redemptions: number;       // 8 pts per redemption processed
  passiveRedemptions: number; // 2 pts per redemption of memberships you sold
  savings: number;           // 10 pts per ₡10,000 saved
  milestones: number;        // Cycle-based milestone points
  achievements: number;      // Permanent achievement points
}

export interface ScoreResult {
  total: number;
  breakdown: ScoreBreakdown;
}

export interface MembershipSales {
  bronce: number;
  plata: number;
  oro: number;
}

export interface ScoreInput {
  membershipSales: MembershipSales;
  redemptionsProcessed: number;     // Redemptions you processed (8 pts each)
  passiveRedemptions: number;       // Redemptions of memberships you sold (2 pts each)
  totalSavingsGenerated: number;    // Total savings in colones
  milestonePoints: number;          // Points from cycle milestones
  achievementPoints: number;        // Points from permanent achievements
}

// Plan points for membership sales
export const PLAN_POINTS = {
  bronce: 10,
  plata: 20,
  oro: 40,
} as const;

// Calculate membership sale points
export const calculateMembershipSalePoints = (sales: MembershipSales): number => {
  return (sales.bronce * PLAN_POINTS.bronce) +
         (sales.plata * PLAN_POINTS.plata) +
         (sales.oro * PLAN_POINTS.oro);
};

// Calculate savings points (10 pts per ₡10,000)
export const calculateSavingsPoints = (totalSavings: number): number => {
  return Math.floor(totalSavings / 10000) * 10;
};

// Main score calculation function
export const calculateScore = (input: ScoreInput): ScoreResult => {
  const membershipSalesPoints = calculateMembershipSalePoints(input.membershipSales);
  const redemptionPoints = input.redemptionsProcessed * 8;
  const passivePoints = input.passiveRedemptions * 2;
  const savingsPoints = calculateSavingsPoints(input.totalSavingsGenerated);
  const milestonePoints = input.milestonePoints;
  const achievementPoints = input.achievementPoints;
  
  const total = membershipSalesPoints + 
                redemptionPoints + 
                passivePoints + 
                savingsPoints + 
                milestonePoints + 
                achievementPoints;
  
  return {
    total,
    breakdown: {
      membershipSales: membershipSalesPoints,
      redemptions: redemptionPoints,
      passiveRedemptions: passivePoints,
      savings: savingsPoints,
      milestones: milestonePoints,
      achievements: achievementPoints,
    },
  };
};

// Legacy function for backwards compatibility during transition
// TODO: Remove after full migration
export interface LegacyScoreInput {
  ventas: number;
  canjes: number;
  trainingCompleted: boolean;
  achievementPoints: number;
}

export interface LegacyScoreBreakdown {
  ventas: number;
  canjes: number;
  training: number;
  logros: number;
}

export interface LegacyScoreResult {
  total: number;
  breakdown: LegacyScoreBreakdown;
}

export const calculateLegacyScore = (input: LegacyScoreInput): LegacyScoreResult => {
  const ventasPoints = input.ventas * 2;
  const canjesPoints = input.canjes;
  const trainingBonus = input.trainingCompleted ? 15 : 0;
  const logrosPoints = input.achievementPoints;
  
  const total = ventasPoints + canjesPoints + trainingBonus + logrosPoints;
  
  return {
    total,
    breakdown: {
      ventas: ventasPoints,
      canjes: canjesPoints,
      training: trainingBonus,
      logros: logrosPoints,
    },
  };
};

// Format score breakdown for display
export const formatScoreBreakdown = (breakdown: ScoreBreakdown): string => {
  const parts: string[] = [];
  if (breakdown.membershipSales > 0) parts.push(`Memb=${breakdown.membershipSales}`);
  if (breakdown.redemptions > 0) parts.push(`Canjes=${breakdown.redemptions}`);
  if (breakdown.passiveRedemptions > 0) parts.push(`Pasivos=${breakdown.passiveRedemptions}`);
  if (breakdown.savings > 0) parts.push(`Ahorro=${breakdown.savings}`);
  if (breakdown.milestones > 0) parts.push(`Hitos=${breakdown.milestones}`);
  if (breakdown.achievements > 0) parts.push(`🏆=${breakdown.achievements}`);
  return parts.join(' + ') || '0';
};

// Format legacy score breakdown for display (backwards compatibility)
export const formatLegacyScoreBreakdown = (breakdown: LegacyScoreBreakdown): string => {
  const parts: string[] = [];
  if (breakdown.ventas > 0) parts.push(`V×2=${breakdown.ventas}`);
  if (breakdown.canjes > 0) parts.push(`C=${breakdown.canjes}`);
  if (breakdown.training > 0) parts.push(`🎓=${breakdown.training}`);
  if (breakdown.logros > 0) parts.push(`🏆=${breakdown.logros}`);
  return parts.join(' + ') || '0';
};

// Milestone definitions for the cycle-based system
export const CYCLE_MILESTONES = {
  first_sale: { name: 'Primera Venta', points: 20, description: 'Primera venta del ciclo' },
  strong_start: { name: 'Inicio Fuerte', points: 30, description: '3 ventas en 24 horas' },
  consistency: { name: 'Constancia', points: 40, description: '1 venta diaria × 5 días' },
  consecutive_3: { name: 'Racha ×3', points: 15, description: 'Cada 3 ventas consecutivas' },
  volume_bronce_10: { name: '10 Bronce', points: 30, description: 'Acumular 10 ventas Bronce' },
  volume_plata_10: { name: '10 Plata', points: 60, description: 'Acumular 10 ventas Plata' },
  volume_oro_10: { name: '10 Oro', points: 120, description: 'Acumular 10 ventas Oro' },
  top_saver: { name: 'Mayor Ahorrador', points: 50, description: 'Más ahorro generado del ciclo' },
} as const;

// Volume bonuses
export const VOLUME_BONUSES = [
  { plan: 'bronce', threshold: 10, points: 30, prize: 12000 },
  { plan: 'plata', threshold: 10, points: 60, prize: 20000 },
  { plan: 'oro', threshold: 10, points: 120, prize: 40000 },
] as const;

// Ranking prizes
export const RANKING_PRIZES = [
  { position: 1, prize: 40000, label: '1er Lugar' },
  { position: 2, prize: 30000, label: '2do Lugar' },
  { position: 3, prize: 20000, label: '3er Lugar' },
] as const;
